import {
  Button,
  Input,
  MessageBar,
  MessageBarBody,
  makeStyles,
  Spinner,
  Text,
  Tooltip,
  tokens,
} from "@fluentui/react-components"
import { WeatherMoon20Regular, WeatherSunny20Regular } from "@fluentui/react-icons"
import { parseIdentifier } from "@rema/shared"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { Navigate, useLocation, useNavigate } from "react-router"
import { useSession } from "../../auth/session"
import { FormField } from "../../components/form-field"
import { es } from "../../i18n/es"
import { isApiError } from "../../lib/api"
import { applyFieldErrors, problemMessage } from "../../lib/problem"
import remafleetLogo from "../../shell/logo-remafleet.png"
import { useTheme } from "../../theme/theme-provider"

const useStyles = makeStyles({
  root: {
    position: "relative",
    display: "grid",
    placeItems: "center",
    height: "100%",
    backgroundColor: tokens.colorNeutralBackground3,
  },
  themeToggle: {
    position: "absolute",
    top: tokens.spacingVerticalM,
    right: tokens.spacingHorizontalM,
  },
  // Card centrada ~440 px — docs/05 §5 (estilo Microsoft sign-in)
  card: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
    width: "min(440px, calc(100vw - 32px))",
    boxSizing: "border-box",
    padding: tokens.spacingHorizontalXXXL,
    borderRadius: tokens.borderRadiusXLarge,
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow8,
  },
  brand: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    color: tokens.colorBrandForeground1,
  },
  // El logo RemaFleet es de texto blanco (pensado para fondos oscuros), así que
  // va sobre un recuadro oscuro para que se lea en cualquier tema.
  logoBox: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#1a1a1a",
    borderRadius: tokens.borderRadiusMedium,
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
  },
  logoImg: {
    height: "26px",
    display: "block",
  },
  subtitle: {
    color: tokens.colorNeutralForeground3,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
  },
})

interface LoginFormValues {
  identifier: string
  password: string
}

interface LocationState {
  from?: string
}

export const LoginPage = () => {
  const styles = useStyles()
  const navigate = useNavigate()
  const location = useLocation()
  const { status, login } = useSession()
  const { mode, toggleMode } = useTheme()
  const [formError, setFormError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const { control, handleSubmit, setError } = useForm<LoginFormValues>({
    // Primera validación en blur; tras un error, re-validación en vivo (docs/05 §3)
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: { identifier: "", password: "" },
  })

  const from = (location.state as LocationState | null)?.from ?? "/"

  if (status === "authenticated") return <Navigate to={from} replace />

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null)
    setSubmitting(true)
    try {
      await login(values.identifier.trim(), values.password)
      navigate(from, { replace: true })
    } catch (error) {
      if (isApiError(error)) {
        // validation_error con errors[] → cada campo en rojo; el resto
        // (invalid_credentials, account_locked…) → MessageBar dentro del formulario
        const mapped = applyFieldErrors(error.problem, setError)
        if (!mapped) setFormError(problemMessage(error.problem))
      } else {
        setFormError(es.errors.unexpected)
      }
    } finally {
      setSubmitting(false)
    }
  })

  const themeLabel = mode === "light" ? es.shell.themeToDark : es.shell.themeToLight

  return (
    <div className={styles.root}>
      <Tooltip content={themeLabel} relationship="label">
        <Button
          appearance="subtle"
          className={styles.themeToggle}
          icon={mode === "light" ? <WeatherMoon20Regular /> : <WeatherSunny20Regular />}
          onClick={toggleMode}
        />
      </Tooltip>
      <section className={styles.card} aria-label={es.login.title}>
        <div className={styles.brand}>
          <span className={styles.logoBox}>
            <img src={remafleetLogo} alt="RemaFleet" className={styles.logoImg} />
          </span>
        </div>
        <Text className={styles.subtitle}>{es.login.subtitle}</Text>
        {formError ? (
          <MessageBar intent="error">
            <MessageBarBody>{formError}</MessageBarBody>
          </MessageBar>
        ) : null}
        <form className={styles.form} onSubmit={onSubmit} noValidate>
          <FormField
            control={control}
            name="identifier"
            label={es.login.identifierLabel}
            required
            rules={{
              required: es.validation.identifierRequired,
              validate: (value) =>
                parseIdentifier(value) !== null || es.validation.identifierFormat,
            }}
          >
            {(field, invalid) => (
              <Input
                {...field}
                type="text"
                autoComplete="username"
                autoFocus
                placeholder={es.login.identifierPlaceholder}
                aria-invalid={invalid}
              />
            )}
          </FormField>
          <FormField
            control={control}
            name="password"
            label={es.login.passwordLabel}
            required
            rules={{ required: es.validation.passwordRequired }}
          >
            {(field, invalid) => (
              <Input
                {...field}
                type="password"
                autoComplete="current-password"
                aria-invalid={invalid}
              />
            )}
          </FormField>
          <Button
            appearance="primary"
            type="submit"
            size="large"
            disabled={submitting}
            icon={submitting ? <Spinner size="tiny" /> : undefined}
          >
            {es.login.submit}
          </Button>
        </form>
      </section>
    </div>
  )
}
